import './CreateEditDialog.scss';

// import { AxiosError } from 'axios';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useRef, useState } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import useAxiosHook from '../../../../../hooks/useAxiosHook';
import useMediaQuery from '../../../../../hooks/useMediaQuery';
import usePrevious from '../../../../../hooks/usePrevious';
import { ClientResource } from '../../../../../types/api/clients';
import { EntityIdRouteParams } from '../../../../../types/routing';
import DialogSpinner from '../../../../Dialogs/DialogSpinner/DialogSpinner';
import FormClientContainer from './FormClientContainer';

type Props = {
  visible: boolean;
  isEditDialog: boolean;
  onHide: () => void;
  reload: () => void;
};

function CreateEditDialog({ visible, isEditDialog, onHide, reload }: Props) {
  const { t } = useTranslation();
  const { id } = useParams<EntityIdRouteParams>();
  const createEditDialogFormRef = useRef<any>(null);
  const [isFormSubmitRequestLoading, setIsFormSubmitRequestLoading] =
    useState(false);

  const { data: configsData, isLoading: isConfigsDataLoading } = useAxiosHook(
    '/configs',
    { skipWhen: !visible }
  );

  const { data: clientData, isLoading: isClientDataLoading } =
    useAxiosHook<ClientResource>(`/clients/${id}`, {
      skipWhen: !visible || !isEditDialog || !id,
    });

  const {
    data: headquarterData,
    isLoading: isHeadquarterDataLoading,
    reload: reloadHeadquarterData,
  } = useAxiosHook<ClientResource>('', {
    skipWhen: !visible || !isEditDialog || !id,
  });

  const { reload: headquarterSubsidiariesReload } = useAxiosHook<
    ClientResource[]
  >('', {
    skipWhen: !visible || !id,
  });

  const prevClientData = usePrevious(clientData);

  useEffect(() => {
    if (
      !clientData ||
      clientData === prevClientData ||
      !clientData.direkcija_id
    ) {
      return;
    }

    reloadHeadquarterData({
      url: `/clients/${clientData.direkcija_id}`,
    });

    headquarterSubsidiariesReload({
      url: `/clients/${clientData.direkcija_id}/subsidiaries`,
    });
  }, [
    clientData,
    headquarterSubsidiariesReload,
    prevClientData,
    reloadHeadquarterData,
  ]);

  const isLoading = isClientDataLoading || isConfigsDataLoading;

  const dialogHeader = isEditDialog
    ? isLoading
      ? t('Loading...')
      : clientData?.ime ?? ''
    : t('Add a client');

  const dialogFooter = isLoading ? null : (
    <>
      <Button
        type="button"
        label={t('Cancel')}
        onClick={() => onHide()}
        className="p-button-secondary p-button-text"
      />

      <Button
        type="submit"
        form="clients-create-edit-form"
        label={
          isFormSubmitRequestLoading
            ? t('Saving...')
            : isEditDialog
            ? t('Save changes')
            : t('Add client')
        }
        disabled={isFormSubmitRequestLoading || isHeadquarterDataLoading}
        data-cy="submit"
      />
    </>
  );

  const isOnMobile = useMediaQuery('(max-width: 768px)');

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={dialogHeader}
      footer={dialogFooter}
      blockScroll
      resizable={false}
      maximizable
      maximized={isOnMobile}
      className={`clients-create-edit-dialog ${isLoading ? 'loading' : ''}`}
    >
      {isLoading ? (
        <DialogSpinner />
      ) : (
        <FormClientContainer
          createEditDialogFormRef={createEditDialogFormRef}
          isEditDialog={isEditDialog}
          clientData={isEditDialog ? clientData : ({} as ClientResource)}
          onLoadingChange={setIsFormSubmitRequestLoading}
          headquarterData={headquarterData}
          configsData={configsData}
          reload={reload}
          onHide={onHide}
          id={id}
        />
      )}
    </Dialog>
  );
}

export default CreateEditDialog;
